import { Link } from "react-router-dom";
import { useArticles } from "../../../hooks/useArticles";
import { toNewsItems } from "../../../lib/articleAdapter";

export default function SustainabilitySection() {
  const { articles, loading } = useArticles("Sostenibilidad");
  const sustainabilityArticles = toNewsItems(articles);
  const [main, second, ...rest] = sustainabilityArticles;

  if (loading) {
    return (
      <section className="bg-white py-6 md:py-12 relative overflow-hidden">
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="h-8 bg-slate-200 rounded w-1/3 mb-5 md:mb-10 animate-pulse" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="lg:col-span-2 h-80 bg-slate-100 rounded-2xl animate-pulse" />
            <div className="space-y-4">
              <div className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
              <div className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
              <div className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white py-6 md:py-12 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute top-10 right-10 w-96 h-96 rounded-full border border-emerald-600" />
        <div className="absolute bottom-10 left-10 w-64 h-64 rounded-full border border-emerald-600" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-emerald-500" />
      </div>

      <div className="max-w-screen-xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5 md:mb-10">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center bg-emerald-600 rounded-xl">
              <i className="ri-leaf-line text-white text-xl" />
            </div>
            <div>
              <span
                className="text-emerald-600 text-xs font-bold tracking-widest uppercase"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Serie Especial
              </span>
              <h2
                className="text-[#0B1F3A] font-extrabold leading-tight"
                style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.9rem" }}
              >
                Sostenibilidad en Aviación
              </h2>
            </div>
          </div>
          <Link
            to="/actualidad"
            className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 border border-emerald-400 hover:border-emerald-500 text-emerald-600 hover:text-emerald-700 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Ver toda la serie <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {sustainabilityArticles.length === 0 ? (
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
              <i className="ri-leaf-line text-slate-400 text-2xl" />
            </div>
            <p className="text-slate-500 text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Aún no hay noticias de sostenibilidad
            </p>
            <p className="text-slate-400 text-xs mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Próximamente se publicarán artículos sobre combustibles verdes, aviación eléctrica y compromisos de carbono neutro
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Main article — large */}
            {main && (
              <Link
                to={`/articulo/${main.slug}`}
                className="lg:col-span-2 relative rounded-2xl overflow-hidden group cursor-pointer h-80 block"
              >
                <img
                  src={main.image}
                  alt={main.title}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="bg-emerald-600 text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                    Destacado
                  </span>
                  {main.isInternational && (
                    <span className="inline-flex items-center gap-1 text-white text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-blue-500/80 border border-blue-400/50 backdrop-blur-sm ml-2">
                      <i className="ri-earth-line text-[10px]" />
                      Internacional
                    </span>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3
                    className="text-white font-bold leading-snug mb-2 group-hover:text-emerald-200 transition-colors"
                    style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.25rem" }}
                  >
                    {main.title}
                  </h3>
                  <p
                    className="text-white/70 text-sm line-clamp-2 mb-3"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {main.excerpt}
                  </p>
                  <div
                    className="flex items-center gap-3 text-xs text-white/50"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    <span>{main.date}</span>
                  </div>
                </div>
              </Link>
            )}

            {/* Side column */}
            <div className="flex flex-col gap-3">
              {[second, ...rest].filter(Boolean).slice(0, 3).map((article) => (
                <Link
                  key={article!.id}
                  to={`/articulo/${article!.slug}`}
                  className="flex gap-3 bg-slate-50 hover:bg-emerald-50 rounded-xl p-3.5 group cursor-pointer transition-all border border-slate-100 hover:border-emerald-200"
                >
                  <div className="flex-shrink-0 w-16 h-14 rounded-lg overflow-hidden">
                    <img
                      src={article!.image}
                      alt={article!.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="bg-emerald-100 text-emerald-700 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full">
                      Sostenibilidad
                    </span>
                    {article!.isInternational && (
                      <span className="inline-flex items-center gap-1 text-blue-600 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 ml-1">
                        <i className="ri-earth-line text-[10px]" />
                        Internacional
                      </span>
                    )}
                    <p
                      className="text-[#0B1F3A] font-semibold text-xs leading-snug mt-1 line-clamp-2 group-hover:text-emerald-700 transition-colors"
                      style={{ fontFamily: "'Sora', sans-serif" }}
                    >
                      {article!.title}
                    </p>
                    <p
                      className="text-slate-400 text-[10px] mt-1"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {article!.date}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}